import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Section } from '../ui/Section';
import { Button } from '../ui/Button';
import { Quote, Star, MessageSquarePlus, X, CheckCircle2, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useTestimonials, addTestimonial } from '../../hooks/useTestimonials';
import { publishToContentful } from '../../lib/contentful';
function toTitleCase(str: string): string {
  return str.replace(/\b\w/g, char => char.toUpperCase());
}
export function TestimonialsSection() {
  const {
    testimonials,
    loading
  } = useTestimonials();
  const [current, setCurrent] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'publishing' | 'success'>('idle');
  const [direction, setDirection] = useState(1);
  const [form, setForm] = useState({
    name: '',
    company: '',
    quote: '',
    rating: 0
  });
  const [ratingError, setRatingError] = useState(false);
  // Clamp current index if testimonials change
  useEffect(() => {
    if (testimonials.length > 0 && current >= testimonials.length) {
      setCurrent(testimonials.length - 1);
    }
  }, [testimonials.length, current]);
  // Auto-rotate
  useEffect(() => {
    if (showForm || testimonials.length <= 1) return;
    const timer = setInterval(() => {
      setDirection(1);
      setCurrent(prev => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length, showForm]);
  const goNext = () => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % testimonials.length);
  };
  const goPrev = () => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  const goTo = (index: number) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.rating === 0) {
      setRatingError(true);
      return;
    }
    setRatingError(false);
    setFormStatus('publishing');
    try {
      // Publish to Contentful CMS
      await publishToContentful({
        contentType: 'testimonials',
        clientName: form.name,
        quote: form.quote,
        rating: form.rating
      });
      // Also save locally for immediate display
      await addTestimonial(form);
      toast.success('Content Live on CMS!');
      setFormStatus('success');
      setTimeout(() => {
        setFormStatus('idle');
        setShowForm(false);
        setForm({
          name: '',
          company: '',
          quote: '',
          rating: 0
        });
      }, 3000);
    } catch (err: any) {
      setFormStatus('idle');
      toast.error(err?.message || 'Failed to submit. Please try again.');
    }
  };
  if (loading) {
    return <Section id="testimonials" background="white" data-id="element-618">
        <div className="flex items-center justify-center py-20 text-gray-400" data-id="element-619">
          <Loader2 className="w-5 h-5 animate-spin mr-2" data-id="element-620" />
          Loading testimonials…
        </div>
      </Section>;
  }
  if (testimonials.length === 0) return null;
  const safeIndex = Math.min(current, testimonials.length - 1);
  return <Section id="testimonials" background="white" data-id="element-621">
      <div className="max-w-4xl mx-auto text-center" data-id="element-622">
        <div className="mb-8 flex justify-center" data-id="element-623">
          <div className="w-12 h-12 bg-[#00316B]/10 rounded-full flex items-center justify-center text-[#00316B]" data-id="element-624">
            <Quote size={24} data-id="element-625" />
          </div>
        </div>

        <div className="relative min-h-[240px] flex flex-col items-center justify-center" data-id="element-626">
          {/* Left Arrow */}
          {testimonials.length > 1 && <button onClick={goPrev} className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors z-10" aria-label="Previous testimonial" data-id="element-627">
              <ChevronLeft className="w-5 h-5" data-id="element-628" />
            </button>}

          {/* Right Arrow */}
          {testimonials.length > 1 && <button onClick={goNext} className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors z-10" aria-label="Next testimonial" data-id="element-629">
              <ChevronRight className="w-5 h-5" data-id="element-630" />
            </button>}

          <div className="px-14" data-id="element-631">
            <AnimatePresence mode="wait" custom={direction} data-id="element-632">
              <motion.div key={safeIndex} custom={direction} initial={{
              opacity: 0,
              x: direction * 40
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction * -40
            }} transition={{
              duration: 0.35,
              ease: 'easeInOut'
            }} className="flex flex-col items-center" data-id="element-633">
                {/* Star Rating */}
                <div className="flex gap-1 mb-6" data-id="element-634">
                  {Array.from({
                  length: 5
                }).map((_, i) => <Star key={i} className={`w-5 h-5 ${i < testimonials[safeIndex].rating ? 'fill-[#F59E0B] text-[#F59E0B]' : 'text-gray-300'}`} data-id="element-635" />)}
                </div>

                <blockquote className="text-2xl md:text-3xl font-heading font-medium text-[#0C0C0C] leading-tight mb-8" data-id="element-636">
                  "{testimonials[safeIndex].quote}"
                </blockquote>

                <div className="font-bold text-[#0C0C0C] text-lg" data-id="element-637">
                  {testimonials[safeIndex].name}
                </div>
                <div className="text-sm text-gray-500 mt-1 font-medium" data-id="element-638">
                  {testimonials[safeIndex].company}
                </div>
                {testimonials[safeIndex].verified && <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#00316B] font-medium" data-id="element-639">
                    <img src="https://cdn.magicpatterns.com/uploads/9mmk6QtVmSzZGQfVenZb4U/badge.svg" alt="Verified" className="w-4 h-4" data-id="element-640" />
                    Verified Testimonial
                  </div>}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-10 mb-12" data-id="element-641">
          {testimonials.map((_, index) => <button key={index} onClick={() => goTo(index)} className={`w-2 h-2 rounded-full transition-all duration-300 ${index === safeIndex ? 'bg-[#00316B] w-6' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to testimonial ${index + 1}`} data-id="element-642" />)}
        </div>

        {/* Public Submission Button / Form */}
        <div className="mt-8" data-id="element-643">
          <AnimatePresence mode="wait" data-id="element-644">
            {!showForm ? <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} data-id="element-645">
                <Button variant="outline" onClick={() => setShowForm(true)} leftIcon={<MessageSquarePlus className="w-4 h-4" data-id="element-647" />} data-id="element-646">
                  Write a Testimonial
                </Button>
              </motion.div> : <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="max-w-lg mx-auto bg-gray-50 rounded-2xl border border-gray-200 p-6 text-left relative overflow-hidden" data-id="element-648">
                <button onClick={() => setShowForm(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" data-id="element-649">
                  <X className="w-5 h-5" data-id="element-650" />
                </button>

                {formStatus === 'success' ? <div className="flex flex-col items-center justify-center py-10 text-center" data-id="element-651">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-652">
                      <CheckCircle2 className="w-6 h-6 text-green-600" data-id="element-653" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-654">
                      Thank You!
                    </h3>
                    <p className="text-gray-600" data-id="element-655">
                      Your testimonial has been submitted.
                    </p>
                  </div> : <>
                    <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-656">
                      Share Your Experience
                    </h3>
                    <form onSubmit={handleSubmit} className="space-y-4" data-id="element-657">
                      <div data-id="element-658">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-659">
                          Your Name <span className="text-red-500" data-id="element-660">*</span>
                        </label>
                        <input type="text" required className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00316B] focus:border-transparent outline-none transition-all" placeholder="e.g. John Doe" value={form.name} onChange={e => setForm({
                    ...form,
                    name: toTitleCase(e.target.value)
                  })} disabled={formStatus === 'publishing'} data-id="element-661" />
                      </div>

                      <div data-id="element-662">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-663">
                          Company / Organization{' '}
                          <span className="text-red-500" data-id="element-664">*</span>
                        </label>
                        <input type="text" required className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00316B] focus:border-transparent outline-none transition-all" placeholder="e.g. Acme Corp" value={form.company} onChange={e => setForm({
                    ...form,
                    company: toTitleCase(e.target.value)
                  })} disabled={formStatus === 'publishing'} data-id="element-665" />
                      </div>

                      <div data-id="element-666">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-667">
                          Rating <span className="text-red-500" data-id="element-668">*</span>
                        </label>
                        <div className="flex gap-1" data-id="element-669">
                          {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => {
                      setForm({
                        ...form,
                        rating: star
                      });
                      setRatingError(false);
                    }} className="focus:outline-none transition-transform active:scale-90" disabled={formStatus === 'publishing'} data-id="element-670">
                              <Star className={`w-6 h-6 ${star <= form.rating ? 'fill-[#F59E0B] text-[#F59E0B]' : 'text-gray-300'}`} data-id="element-671" />
                            </button>)}
                        </div>
                        {ratingError && <p className="text-xs text-red-500 mt-1" data-id="element-672">
                            Please select a rating
                          </p>}
                      </div>

                      <div data-id="element-673">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-674">
                          Testimonial <span className="text-red-500" data-id="element-675">*</span>
                        </label>
                        <textarea required rows={3} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00316B] focus:border-transparent outline-none transition-all resize-none" placeholder="How was your experience working with us?" value={form.quote} onChange={e => setForm({
                    ...form,
                    quote: e.target.value
                  })} disabled={formStatus === 'publishing'} data-id="element-676" />
                      </div>

                      <div className="pt-2" data-id="element-677">
                        <Button type="submit" variant="secondary" className="w-full" isLoading={formStatus === 'publishing'} disabled={formStatus === 'publishing'} data-id="element-678">
                          {formStatus === 'publishing' ? 'Publishing…' : 'Submit Testimonial'}
                        </Button>
                      </div>
                    </form>
                  </>}
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </Section>;
}